import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control, UseFormRegister, FieldErrors } from 'react-hook-form';
import { CollapsibleSection } from '@roger/ui-components';
import { Field } from '../../FormFields';
import { formatPhone, formatZipCode } from '../../../utils/intake';
import type { CreateEpisodeStepFormData } from '../../../schemas/createEpisodeStepFormSchema';

interface EmergencyContactSectionProps {
  control: Control<CreateEpisodeStepFormData>;
  register: UseFormRegister<CreateEpisodeStepFormData>;
  errors: FieldErrors<CreateEpisodeStepFormData>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;
}

export const EmergencyContactSection: React.FC<
  EmergencyContactSectionProps
> = ({ control, register, errors, handleFieldBlur }) => {
  return (
    <CollapsibleSection title='Emergency Contact'>
      <div className='grid grid-cols-4 gap-3'>
        <Field
          label='Contact Name'
          htmlFor='emergency_contact_name'
          error={errors.emergency_contact_name?.message}
          inputProps={register('emergency_contact_name', {
            onBlur: handleFieldBlur,
          })}
        />
        <Field
          label='Relationship'
          htmlFor='emergency_contact_relationship'
          error={errors.emergency_contact_relationship?.message}
          inputProps={register('emergency_contact_relationship', {
            onBlur: handleFieldBlur,
          })}
        />
        <Controller
          name='emergency_contact_phone'
          control={control}
          render={({ field }) => (
            <Field
              label='Phone'
              htmlFor='emergency_contact_phone'
              error={errors.emergency_contact_phone?.message}
              maintainCursorPosition
              inputProps={{
                ...field,
                value: field.value || '',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatPhone(e.target.value);
                  field.onChange(formatted);
                },
                onBlur: handleFieldBlur,
              }}
            />
          )}
        />
        <div className='col-span-1' />

        <div className='col-span-2'>
          <Field
            label='Street Address'
            htmlFor='emergency_contact_street_address'
            error={errors.emergency_contact_street_address?.message}
            inputProps={register('emergency_contact_street_address', {
              onBlur: handleFieldBlur,
            })}
          />
        </div>
        <Field
          label='City'
          htmlFor='emergency_contact_city'
          error={errors.emergency_contact_city?.message}
          inputProps={register('emergency_contact_city', {
            onBlur: handleFieldBlur,
          })}
        />
        <Field
          label='State'
          htmlFor='emergency_contact_state'
          error={errors.emergency_contact_state?.message}
          inputProps={register('emergency_contact_state', {
            onBlur: handleFieldBlur,
          })}
        />
        <Controller
          name='emergency_contact_zip_code'
          control={control}
          render={({ field }) => (
            <Field
              label='Zip Code'
              htmlFor='emergency_contact_zip_code'
              error={errors.emergency_contact_zip_code?.message}
              maintainCursorPosition
              inputProps={{
                ...field,
                value: field.value || '',
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatZipCode(e.target.value);
                  field.onChange(formatted);
                },
                onBlur: handleFieldBlur,
              }}
            />
          )}
        />
      </div>
    </CollapsibleSection>
  );
};
